/**
 * Origin-scoped storage for the Epic Encounter id used with Redox write-back.
 *
 * IMDB round-trips may omit `canonicalOrder.ehrContext` on `json_payload`; the encounter
 * is still needed on in-progress PUTs for `access_token` / `ehr_encounter_id`. We persist it when:
 * - Successful Epic submit (OrderReadyToSend), and
 * - Successful GET order by id / by patient (`DatabaseOperations`), whenever the payload
 *   includes `canonicalOrder.ehrContext.ehrEncounterId` or `orderFormInfo.epicEncounter.id`.
 *
 * Stored in `localStorage` (not `sessionStorage`) so the cache survives across browser
 * windows of the same origin. This matters for Epic launches in floating-window mode,
 * where each window has its own `sessionStorage` and would otherwise miss the encounter
 * binding written during submit in another window. The encounter id is a non-secret EHR
 * resource id, so origin-scoped persistence is acceptable.
 */

const STORAGE_KEY = 'epic_smart_redox_ehr_encounter_by_order_id'

function readMap(): Record<string, string> {
  if (typeof window === 'undefined') return {}

  // 1. Read current localStorage (the canonical store going forward).
  let current: Record<string, string> = {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, string>
      if (parsed && typeof parsed === 'object') current = parsed
    }
  } catch {
    // ignore corrupt storage
  }

  // 2. ALWAYS check sessionStorage for legacy entries, even if localStorage is
  // already populated. Epic floating-window mode gives each window its own
  // sessionStorage; if we early-returned on a non-empty localStorage, only the
  // first window to run after deploy would migrate, and every other window
  // would silently drop its own sessionStorage encounter bindings.
  let parsedLegacy: Record<string, string> | null = null
  try {
    const legacy = sessionStorage.getItem(STORAGE_KEY)
    if (legacy) {
      const parsed = JSON.parse(legacy) as Record<string, string>
      if (parsed && typeof parsed === 'object') parsedLegacy = parsed
    }
  } catch {
    // ignore corrupt legacy storage
  }

  if (!parsedLegacy) return current

  // 3. Merge with localStorage taking precedence on key collision (canonical
  // store wins). Promotion to localStorage and sessionStorage cleanup are
  // best-effort: on quota / Safari ITP / private mode, the in-memory merged
  // map is still returned so the caller does not lose the encounter binding.
  const merged: Record<string, string> = { ...parsedLegacy, ...current }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore promotion failures; merged data still returned
  }
  return merged
}

function writeMap(map: Record<string, string>): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // ignore quota / serialization failures; cache is best-effort
  }
}

function encounterIdFromJsonPayload(jsonPayload: unknown): string | null {
  if (!jsonPayload || typeof jsonPayload !== 'object') return null
  const jp = jsonPayload as Record<string, unknown>

  const co = jp.canonicalOrder
  if (co && typeof co === 'object') {
    const ctx = (co as Record<string, unknown>).ehrContext
    if (ctx && typeof ctx === 'object') {
      const eid = (ctx as Record<string, unknown>).ehrEncounterId
      if (typeof eid === 'string' && eid.trim()) return eid.trim()
    }
  }

  const ofi = jp.orderFormInfo
  if (ofi && typeof ofi === 'object') {
    const ee = (ofi as Record<string, unknown>).epicEncounter
    if (ee && typeof ee === 'object') {
      const id = (ee as Record<string, unknown>).id
      if (typeof id === 'string' && id.trim()) return id.trim()
    }
  }

  return null
}

/**
 * After GET `/database/orders/{id}` or GET `/database/orders/patient/...`, cache encounter
 * for Redox PUTs. Visible to all windows of the same origin.
 */
export function syncEpicEhrEncounterFromOrderGet(order: { id?: unknown; json_payload?: unknown }): void {
  const oid = order.id != null ? String(order.id).trim() : ''
  if (!oid) return
  const enc = encounterIdFromJsonPayload(order.json_payload)
  if (enc) {
    setEpicEhrEncounterForOrder(oid, enc)
  }
}

export function setEpicEhrEncounterForOrder(orderId: string, encounterId: string): void {
  if (typeof window === 'undefined') return
  const oid = orderId.trim()
  const eid = encounterId.trim()
  if (!oid || !eid) return
  const map = readMap()
  if (map[oid] === eid) return
  map[oid] = eid
  writeMap(map)
}

export function getEpicEhrEncounterForOrder(orderId: string): string | null {
  if (typeof window === 'undefined') return null
  const oid = orderId.trim()
  if (!oid) return null
  const map = readMap()
  const v = map[oid]
  return typeof v === 'string' && v.trim() ? v.trim() : null
}
